"use client"
import { useState, useEffect } from "react"
import { cn } from "../lib/utils" // or wherever your utils are
import { LayoutDashboard, Clock, UserCheck, Calendar, CalendarPlus, BriefcaseBusiness, BarChart3, Hourglass, SettingsIcon, Users, Network, FileText, CheckCircle } from 'lucide-react'
import { Link, useLocation } from "react-router-dom"

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  roleId: string;
}

const rolePermissions: Record<number, string[]> = {
  1: ["dashboard", "approver", "leave-management", "leave-request-approvals"], // HR
  2: ["dashboard", "approvals", "engagements"], // Manager
  3: ["dashboard", "timesheet", "leaves", "reports"], // Employee
  4: ["config-settings", "system-users", "menu-role-mapping", "templates"], // Admin
  6: ["approvals"], // Secondary Approver - no dashboard, only page access
  7: ["engagements"], // CoOwner - no dashboard, only page access
};

export function Sidebar({ isOpen, onToggle }: SidebarProps) {


  const menuItems = [
    { id: "dashboard", title: "Dashboard", icon: LayoutDashboard, href: "/dashboard" },
    { id: "timesheet", title: "Add/View Timesheet", icon: Clock, href: "/timesheet" },
    { id: "leaves", title: "View Leave History", icon: CalendarPlus, href: "/add-view-leaves" },
    { id: "approver", title: "Assign Approver", icon: UserCheck, href: "/assign-approver" },
    { 
      id: "leave-management", 
      title: "Leave Management", 
      icon: Calendar, 
      href: "/leave-management/leave-policies",
      subItems: [
        { id: "leave-policy", title: "Leave Policy", href: "/leave-management/leave-policies" },
        { id: "leave-summary", title: "Leave Summary", href: "/leave-management/leave-summary" },
        { id: "user-policy-mapping", title: "User Policy Mapping", href: "/leave-management/user-policy-mapping" }
      ]
    },
    { id: "leave-request-approvals", title: "Leave Request Approvals", icon: CheckCircle, href: "/leave-request-approvals" },
    { 
      id: "approvals", 
      title: "Review & Approvals", 
      icon: Hourglass, 
      href: "/pending-approvals/leave",
      subItems: [
        { id: "leave-approvals", title: "Leave Approvals", href: "/pending-approvals/leave" },
        { id: "timesheet-approvals", title: "Timesheet Approvals", href: "/pending-approvals/timesheet" }
      ]
    },
    { id: "engagements", title: "Manage Engagements", icon: BriefcaseBusiness, href: "/manage-engagements" },
    { id: "reports", title: "Reports", icon: BarChart3, href: "/reports" },
    { id: "config-settings", title: "Config Settings", icon: SettingsIcon, href: "/config-settings" },
    { id: "system-users", title: "System Users", icon: Users, href: "/system-users" },
    { id: "menu-role-mapping", title: "Menu Role Mapping", icon: Network, href: "/menu-role-mapping" },
    { id: "templates", title: "Templates", icon: FileText, href: "/templates" },
  ]

  const location = useLocation()
  const [isApprovalsExpanded, setIsApprovalsExpanded] = useState(false)
  const [isLeaveManagementExpanded, setIsLeaveManagementExpanded] = useState(false)

  // Auto-expand sub-menu if on a sub-menu page
  useEffect(() => {
    if (location.pathname.includes("/pending-approvals/")) {
      setIsApprovalsExpanded(true)
    }
    if (location.pathname.includes("/leave-management/")) {
      setIsLeaveManagementExpanded(true)
    }
  }, [location.pathname])

  
  // Get roles from localStorage
  const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]") as number[];

  const roles = storedRoles
  
  // If Admin (4), allow all modules
  const allowedItems = Array.from(
  new Set(roles.flatMap(r => rolePermissions[r] || []))
);

console.log("RAW localStorage roles:", localStorage.getItem("roles"));
console.log("PARSED roles:", storedRoles);
console.log("Allowed items:", allowedItems);


  ////console.log("Allowed items based on roles:", roles, allowedItems);

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}

      <div
        className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 z-40 shadow-lg transition-transform duration-300 ease-in-out"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <nav className="p-4 space-y-2 overflow-y-auto h-full">
 
          {menuItems
            .filter(item => allowedItems.includes(item.id))
            .map((item) => {
              const isActive = location.pathname === item.href || (item.subItems && item.subItems.some(sub => location.pathname === sub.href))
              const hasSubItems = item.subItems && item.subItems.length > 0
              const isApprovalsItem = item.id === "approvals"
              const isLeaveManagementItem = item.id === "leave-management"
              const shouldShowSubItems = isApprovalsItem ? isApprovalsExpanded : (isLeaveManagementItem ? isLeaveManagementExpanded : true)
              
              return (
                <div key={item.id}>
                  {(isApprovalsItem || isLeaveManagementItem) ? (
                    <Link
                      to={item.href}
                      onClick={() => {
                        if (isApprovalsItem) {
                          setIsApprovalsExpanded(true)
                          setIsLeaveManagementExpanded(false)
                        }
                        if (isLeaveManagementItem) {
                          setIsLeaveManagementExpanded(true)
                          setIsApprovalsExpanded(false)
                        }
                      }}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.title}
                    </Link>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      onClick={() => {
                        // Close sub-menus when clicking other items
                        setIsApprovalsExpanded(false)
                        setIsLeaveManagementExpanded(false)
                        // Close sidebar on mobile after navigation
                        if (window.innerWidth < 1024) {
                          onToggle()
                        }
                      }}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.title}
                    </Link>
                  )}
                  
                  {/* Sub-menu items */}
                  {hasSubItems && item.subItems && shouldShowSubItems && (
                    <div className="ml-4 mt-1">
                      {item.subItems.map((subItem) => {
                        const isSubActive = location.pathname === subItem.href
                        return (
                          <Link
                            key={subItem.id}
                            to={subItem.href}
                            className={cn(
                              "flex items-center gap-3 px-8 py-2 text-sm font-medium transition-colors",
                              isSubActive
                                ? "text-blue-700"
                                : "text-gray-600 hover:text-gray-900"
                            )}
                            onClick={() => {
                              // Close the other main menu when clicking a submenu item
                              if (isApprovalsItem) {
                                setIsLeaveManagementExpanded(false)
                              }
                              if (isLeaveManagementItem) {
                                setIsApprovalsExpanded(false)
                              }
                              // Close sidebar on mobile after navigation
                              if (window.innerWidth < 1024) {
                                onToggle()
                              }
                            }}
                          >
                            {subItem.title}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
        </nav>
      </div>
    </>
  )
}
